import React, { useState, useEffect, useRef } from "react";
import { URLS } from "../Services/Constant";
import { post } from "../Services/ApiHandler";
import useStore from "../Screen/redux/store/useStore";
import SubmitButton from "./SubmitButton";

const ChatModal = ({ visible, onClose, chatId }) => {
  const { setMessages, messages } = useStore();
  const message = messages.list;

  const messageLoaded = useRef(false);
  const tempIdRef = useRef(null);
  const messageRef = useRef([]);
  const isFetching = useRef(false);
  const isSendFetching = useRef(false);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  useEffect(() => {
    scrollToBottom();
  }, [message.length]);

  // ─── Initial load when chat opens ───
  useEffect(() => {
    if (!chatId) return;

    messageLoaded.current = false;
    tempIdRef.current = null;
    isFetching.current = false;
    isSendFetching.current = false;
    setMessages("list", []);
    messageRef.current = [];
    setMessages("onChat", true);
    setMessages("unread", 0);

    fetchHistory();

    return () => {
      setMessages("onChat", false);
    };
  }, [chatId]);

  // ─── Watch unread count — fires when admin sends message ───
  useEffect(() => {
    const unread = messages?.unread;
    if (!unread || unread === 0) return;
    if (!messageLoaded.current) return;
    if (isSendFetching.current) return; // ← block if send is already fetching

    setMessages("unread", 0);
    fetchHistory();
  }, [messages?.unread]);

  const fetchHistory = async () => {
    if (isFetching.current) return;
    isFetching.current = true;

    try {
      const res = await post(URLS.CHATHISTORY, { client_id: chatId });
      if (res?.status === "success") {
        storeMessages(res?.data ?? []);
      }
    } catch {
      // silent fail
    } finally {
      isFetching.current = false;
    }
  };

  const storeMessages = (historyData) => {
    const seen = new Set();
    const deduped = historyData.filter((m) => {
      if (!m.id || seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });

    tempIdRef.current = null;
    messageRef.current = deduped;
    setMessages("list", deduped);
    messageLoaded.current = true;
    setTimeout(scrollToBottom, 100);
  };

  const scrollToBottom = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  const handleSend = async () => {
  if (input.trim() === "" || loading) return;

  const messageText = input.trim();
  setInput("");
  setLoading(true);
  isSendFetching.current = true;

  const payload = new FormData();
  payload.append("client_id", chatId);
  payload.append("message", messageText);

  try {
    const res = await post(URLS.SUPPORTCHAT, payload);
    if (res?.status === "success") {
      await fetchHistory(); // ← just fetch, no optimistic row at all
    }
  } catch {
    // silent
  } finally {
    isSendFetching.current = false;
    setLoading(false);
    setTimeout(scrollToBottom, 50);
  }
};

  return (
    <div
      className="chat-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="chat-container">
        <h2 className="chat-header">Help & Support</h2>

        <div className="chat-area" ref={scrollRef}>
          {message.length === 0 ? (
            <p
              className="text-center f2 py-4"
              style={{ color: "var(--color-text-secondary)" }}
            >
              No messages yet. Say hello!
            </p>
          ) : (
            message.map((item) => {
              const isAdmin =
                item.is_admin_sender === 1 || item.is_admin_sender === true;
              return (
                <div
                  key={item.id}
                  className={`message-container ${isAdmin ? "admin" : "user"}`}
                >
                  {isAdmin && <div className="sender-text">Admin</div>}
                  <div className="message-box">
                    <div className="message-text f3">{item.message}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="input-row">
          <input
            className="chat-input"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && handleSend()}
          />
        </div>

        <SubmitButton
          onClick={handleSend}
          title="Send to Admin"
          disabled={loading}
          isFormValid={input.trim() === ""}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ChatModal;